export type VerificationReportCheckStatus = 'passed' | 'open' | 'not-run';

/**
 * What a check's result means to a reviewer, finer than `status`:
 * - failed:      it ran and found something wrong with the company itself
 * - review:      it ran and something does not line up
 * - input:       it cannot run, because a field it reads is empty
 * - unavailable: it cannot run for this record (the registry is Canada-only)
 * - not-run:     it could run, but has no result yet
 */
export type VerificationReportCheckTone =
  | 'passed'
  | 'failed'
  | 'review'
  | 'input'
  | 'unavailable'
  | 'not-run';

export type VerificationRecordField =
  | 'name'
  | 'country'
  | 'registration'
  | 'email'
  | 'phone'
  | 'address'
  | 'domain';

export interface VerificationReportDetail {
  label: string;
  value: string;
}

export interface VerificationReportCheck {
  id: string;
  label: string;
  status: VerificationReportCheckStatus;
  tone: VerificationReportCheckTone;
  /** Pill text when the check is not passing. */
  openLabel: string;
  /** One line under the label: the evidence, or why there is none. */
  description: string;
  /** What the check reads, shown before anything has run. */
  about: string;
  /** Prose in the expanded row: what was found, or what closes it. */
  next: string;
  /** The record field whose edit would close this check, if any. */
  fix: VerificationRecordField | null;
  reads: VerificationRecordField[];
  details: VerificationReportDetail[];
  checkedAt: string | null;
}

export interface VerificationRecord {
  name?: string;
  domain?: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  address1?: string;
  address2?: string;
  country_of_origin?: string;
  business_registration_number?: string;
  domain_age_info?: unknown;
  domain_reputation_info?: unknown;
  email_verify_info?: unknown;
  phone_verify_info?: unknown;
  website_quality_info?: unknown;
  places_verification_info?: unknown;
  cbr_registry_info?: unknown;
  domain_age_checked_at?: string | null;
  domain_reputation_checked_at?: string | null;
  email_verify_checked_at?: string | null;
  phone_verify_checked_at?: string | null;
  website_quality_checked_at?: string | null;
  places_verification_checked_at?: string | null;
  cbr_registry_checked_at?: string | null;
}

export const VERIFICATION_FIELD_LABELS: Record<
  VerificationRecordField,
  string
> = {
  name: 'Name',
  country: 'Entity country',
  registration: 'Business registration / EIN',
  email: 'Contact email',
  phone: 'Contact phone',
  address: 'Address',
  domain: 'Domain',
};

export const VERIFICATION_FIX_LABELS: Record<VerificationRecordField, string> =
  {
    name: 'Edit name',
    country: 'Add country',
    registration: 'Add registration number',
    email: 'Add contact email',
    phone: 'Edit contact phone',
    address: 'Edit address',
    domain: 'Add domain',
  };

const asObject = (value: unknown): any | null => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const text = (value: unknown, fallback = 'Not available') => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const risk = (value: any) =>
  String(
    value?.overall_risk ??
      value?.overallRisk ??
      value?.risk ??
      value?.status ??
      '',
  ).toLowerCase();

const isLowRisk = (value: any) =>
  ['low', 'safe', 'pass', 'passed', 'clear'].includes(risk(value));

const isHighRisk = (value: any) =>
  ['high', 'critical', 'fail', 'failed'].includes(risk(value));

const hasResult = (value: any | null) =>
  Boolean(value && Object.keys(value).length > 0);

const latest = (...values: Array<string | null | undefined>) => {
  const times = values
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter((time) => Number.isFinite(time));
  return times.length ? new Date(Math.max(...times)).toISOString() : null;
};

// `status` keeps its original meaning for the record-page band: a check that
// has no result (for whatever reason) is 'not-run', and the band decides from
// that whether the record has ever been verified.
const statusFor = (
  tone: VerificationReportCheckTone,
): VerificationReportCheckStatus =>
  tone === 'passed'
    ? 'passed'
    : tone === 'failed' || tone === 'review'
    ? 'open'
    : 'not-run';

const openLabelFor = (tone: VerificationReportCheckTone, failed = 'Failed') =>
  ({
    passed: 'Passed',
    failed: failed,
    review: 'Needs review',
    input: 'Needs input',
    unavailable: 'Not available',
    'not-run': 'Not run',
  }[tone]);

export const isCanadianRecord = (record: VerificationRecord): boolean =>
  ['canada', 'ca', 'can'].includes(
    String(record?.country_of_origin || '')
      .trim()
      .toLowerCase(),
  );

export const recordAddress = (record: VerificationRecord): string =>
  record?.address ||
  [record?.address1, record?.address2].filter(Boolean).join(', ');

export const recordDomain = (record: VerificationRecord): string =>
  record?.domain || record?.website || '';

export const recordFieldValue = (
  record: VerificationRecord,
  field: VerificationRecordField,
): string => {
  const value = {
    name: record?.name,
    country: record?.country_of_origin,
    registration: record?.business_registration_number,
    email: record?.email,
    phone: record?.phone,
    address: recordAddress(record),
    domain: recordDomain(record),
  }[field];
  return value ? String(value).trim() : '';
};

export const buildEntityVerificationReportChecks = (
  supplier: VerificationRecord,
): VerificationReportCheck[] => {
  const domainAge = asObject(supplier?.domain_age_info);
  const domainReputation = asObject(supplier?.domain_reputation_info);
  const email = asObject(supplier?.email_verify_info);
  const phone = asObject(supplier?.phone_verify_info);
  const website = asObject(supplier?.website_quality_info);
  const places = asObject(supplier?.places_verification_info);
  const registration = asObject(supplier?.cbr_registry_info);
  const domain = recordDomain(supplier);
  const address = recordAddress(supplier);
  const country = supplier?.country_of_origin || '';

  // Domain & contact: registrar, age and reputation of the company's domain.
  const domainYears = Number(domainAge?.domain_age_in_years);
  const reputationScore = Number(domainReputation?.risk_score?.result);
  const domainRegistered = String(domainAge?.domain_registered).toLowerCase();
  const domainTone: VerificationReportCheckTone = !domain
    ? 'input'
    : !hasResult(domainAge) && !hasResult(domainReputation)
    ? 'not-run'
    : domainRegistered === 'no' ||
      (Number.isFinite(reputationScore) && reputationScore >= 75)
    ? 'failed'
    : domainRegistered === 'yes' &&
      Number.isFinite(domainYears) &&
      domainYears >= 2 &&
      Number.isFinite(reputationScore) &&
      reputationScore <= 30
    ? 'passed'
    : 'review';
  const domainAgeText = Number.isFinite(domainYears)
    ? `registered ${domainYears} ${domainYears === 1 ? 'year' : 'years'}`
    : 'registration date unknown';
  const reputationText = Number.isFinite(reputationScore)
    ? `reputation risk ${reputationScore} of 100`
    : 'reputation not scored';

  // Listing consistency: whether the public listing agrees with this record.
  const locationChecks = places?.checks?.locationVerification;
  const listedPhoneMatches =
    places?.phone_match_status !== 'fail' &&
    locationChecks?.hasMatchingPhone?.value !== false;
  const listedNameMatches =
    !['fail', 'denied'].includes(places?.business_name_match_status) &&
    locationChecks?.hasMatchingBusinessName?.value !== false;
  // A pass needs a listing that was actually found, not just a low risk score.
  const listingFound =
    places?.place_found === true ||
    places?.googleResponse?.placeFound === true;
  const listingMissing =
    places?.place_found === false ||
    places?.googleResponse?.placeFound === false;
  const listingTone: VerificationReportCheckTone = !address
    ? 'input'
    : !hasResult(places)
    ? 'not-run'
    : listingFound &&
      !listingMissing &&
      isLowRisk(places) &&
      listedPhoneMatches &&
      listedNameMatches
    ? 'passed'
    : 'review';
  const listingMismatch = listingMissing
    ? 'No public listing was found for this address'
    : !listingFound
    ? 'The lookup did not say whether a public listing exists for this address'
    : !listedPhoneMatches && !listedNameMatches
    ? 'A public listing was found, but its name and phone do not match'
    : !listedPhoneMatches
    ? 'A public listing was found, but its phone number does not match'
    : !listedNameMatches
    ? 'A public listing was found, but under a different business name'
    : 'The public listing does not fully match this record';

  // Website contact presence.
  const websiteTone: VerificationReportCheckTone = !domain
    ? 'input'
    : !hasResult(website)
    ? 'not-run'
    : isLowRisk(website)
    ? 'passed'
    : isHighRisk(website)
    ? 'failed'
    : 'review';

  // Business registration: the registry lookup covers Canadian companies.
  const canRegistry = isCanadianRecord(supplier);
  // Checked before any stored result: verification never re-runs the registry
  // once the record is no longer Canadian, so that result would never refresh.
  // No registry match is never a pass, whatever the aggregate risk says.
  const registrationTone: VerificationReportCheckTone = !canRegistry
    ? 'unavailable'
    : !hasResult(registration)
    ? 'not-run'
    : registration?.match_found === false
    ? 'review'
    : registration?.is_active === false
    ? 'failed'
    : isLowRisk(registration) ||
      (registration?.match_found === true &&
        registration?.is_active !== false &&
        registration?.bn_age_pass !== false)
    ? 'passed'
    : 'review';

  // Email verification.
  const emailScore = Number(email?.score);
  const emailTone: VerificationReportCheckTone = !supplier?.email
    ? 'input'
    : !hasResult(email)
    ? 'not-run'
    : email?.valid_format === false ||
      email?.disposable === true ||
      email?.should_block === true
    ? 'failed'
    : // A pass needs every signal answered; a partial response is reviewed.
    email?.valid_format === true &&
      email?.has_mx_records === true &&
      email?.valid_tld === true &&
      email?.is_spoofable === false &&
      email?.dmarc_enforced === true &&
      email?.free_email === false &&
      email?.suspicious_domain === false &&
      email?.disposable === false &&
      Number.isFinite(emailScore) &&
      emailScore >= 70
    ? 'passed'
    : 'review';
  const emailFlags = [
    email?.valid_format === true
      ? 'valid format'
      : email?.valid_format === false
      ? 'invalid format'
      : 'format unknown',
    email?.has_mx_records === true
      ? 'MX records present'
      : email?.has_mx_records === false
      ? 'no MX records'
      : 'MX records unknown',
    email?.disposable === true
      ? 'disposable'
      : email?.disposable === false
      ? 'not disposable'
      : 'disposable status unknown',
    email?.free_email === true ? 'free provider' : '',
  ].filter(Boolean);
  // Why a deliverable address still needs review, beyond the flags above.
  const emailReviewReasons = [
    Number.isFinite(emailScore) && emailScore < 70
      ? `Its score of ${emailScore} is under the 70 needed to pass`
      : '',
    email?.is_spoofable === true ? 'Mail from its domain can be spoofed' : '',
    email?.dmarc_enforced === false ? 'Its domain does not enforce DMARC' : '',
    email?.suspicious_domain === true ? 'Its domain looks suspicious' : '',
    email?.valid_tld === false ? 'Its top-level domain is not recognised' : '',
  ].filter(Boolean);

  // Phone validation.
  const phoneTone: VerificationReportCheckTone = !supplier?.phone
    ? 'input'
    : !hasResult(phone)
    ? 'not-run'
    : phone?.valid === false || phone?.abusive === true
    ? 'failed'
    : phone?.valid === true && phone?.disposable !== true
    ? 'passed'
    : 'review';
  const phoneFacts = [
    phone?.line_type ? String(phone.line_type).toLowerCase() : '',
    phone?.carrier,
    [phone?.city, phone?.region].filter(Boolean).join(' '),
  ].filter(Boolean);

  // Address & facility.
  const addressValidation = places?.address_validation;
  const newAddressChecks = places?.checks?.addressValidation;
  const isPoBox =
    addressValidation?.is_po_box === true ||
    newAddressChecks?.isPoBox?.value === true;
  const addressComplete =
    addressValidation?.address_complete !== false &&
    newAddressChecks?.isCompleteAddress?.value !== false;
  const addressConfirmed =
    addressValidation?.has_unconfirmed_components !== true &&
    newAddressChecks?.hasUnconfirmedComponents?.value !== true;
  const addressTone: VerificationReportCheckTone = !address
    ? 'input'
    : !hasResult(places)
    ? 'not-run'
    : listingFound &&
      !listingMissing &&
      addressComplete &&
      addressConfirmed &&
      !isPoBox
    ? 'passed'
    : 'review';
  const addressProblem = isPoBox
    ? 'a P.O. box, not a building'
    : !addressComplete
    ? 'incomplete, so it could not be matched to a building'
    : !addressConfirmed
    ? 'only partly confirmed by the address service'
    : 'not matched to a commercial listing';

  const pick = <T>(
    tone: VerificationReportCheckTone,
    map: Partial<Record<VerificationReportCheckTone, T>>,
    fallback: T,
  ) => map[tone] ?? fallback;

  return [
    {
      id: 'domain-contact',
      label: 'Domain & contact',
      tone: domainTone,
      status: statusFor(domainTone),
      openLabel: openLabelFor(domainTone, 'High risk'),
      about: 'Registrar, age and reputation of the company’s own domain',
      description: pick(
        domainTone,
        {
          input:
            'No domain on file, so registrar and reputation could not be checked',
          'not-run': `${domain} has not been checked yet`,
        },
        `${domain} · ${domainAgeText} · ${reputationText}`,
      ),
      next: pick(
        domainTone,
        {
          input:
            'Add the company’s domain to this record, then run verification again. The registrar, domain age and reputation are read from it, and the same domain also closes Website contact presence.',
          'not-run':
            'The registrar, domain age and reputation are read on the next run.',
          passed:
            'At least two years old with a low reputation score, so it reads as the company’s established domain.',
          failed:
            domainRegistered === 'no'
              ? 'The domain is not registered to anyone. A company that trades under an unregistered domain cannot receive mail there. Confirm the domain with the company.'
              : 'A reputation score this high is common in impersonation and payment fraud. Confirm the domain belongs to the company before relying on it; the score is read again on every run.',
        },
        'A domain younger than two years or scoring above 30 needs a reviewer to confirm it belongs to the company.',
      ),
      fix: domainTone === 'input' ? 'domain' : null,
      reads: ['domain'],
      checkedAt: latest(
        supplier?.domain_age_checked_at,
        supplier?.domain_reputation_checked_at,
      ),
      details: [
        { label: 'Registered', value: text(domainAge?.domain_registered) },
        {
          label: 'Domain age',
          value: Number.isFinite(domainYears)
            ? `${domainYears} years`
            : 'Not available',
        },
        {
          label: 'Reputation risk score',
          value: text(domainReputation?.risk_score?.result),
        },
      ],
    },
    {
      id: 'listing-consistency',
      label: 'Listing consistency',
      tone: listingTone,
      status: statusFor(listingTone),
      openLabel: openLabelFor(listingTone),
      about: 'Whether public listings agree with the details on this record',
      description: pick(
        listingTone,
        {
          input: 'No address on file, so a public listing could not be checked',
          'not-run': 'The public business listing has not been checked yet',
          passed:
            places?.summary ||
            'The public listing matches the name and phone on file',
        },
        places?.summary || listingMismatch,
      ),
      next: pick(
        listingTone,
        {
          input:
            'Add the company’s address to this record, then run verification again. The public listing for that address is compared with the name and phone here.',
          'not-run':
            'The public listing for this address is compared with the name and phone on the next run.',
          passed:
            'The public listing for this address carries the same business name and phone number as the record.',
        },
        `${
          places?.summary ? `${listingMismatch}. ` : ''
        }Confirm which details are current and update the record if it is out of date; the listing is read again on the next run.`,
      ),
      fix:
        listingTone === 'input'
          ? 'address'
          : listingTone === 'review' && !listedPhoneMatches
          ? 'phone'
          : listingTone === 'review'
          ? 'address'
          : null,
      reads: ['name', 'phone', 'address'],
      checkedAt: supplier?.places_verification_checked_at || null,
      details: [
        {
          label: 'Listing found',
          value: text(
            places?.place_found ?? places?.googleResponse?.placeFound,
          ),
        },
        {
          label: 'Listing name',
          value: text(
            places?.google_place_name ??
              places?.googleResponse?.foundBusiness?.name,
          ),
        },
        {
          label: 'Listing phone',
          value: text(
            places?.google_phone ??
              places?.googleResponse?.foundContact?.phoneNumber,
          ),
        },
      ],
    },
    {
      id: 'website-contact',
      label: 'Website contact presence',
      tone: websiteTone,
      status: statusFor(websiteTone),
      openLabel: openLabelFor(websiteTone, 'High risk'),
      about: 'Whether the contacts published on the site match this record',
      description: pick(
        websiteTone,
        {
          input:
            'No domain on file, so published contact details could not be compared',
          'not-run': 'Website contact presence has not been checked yet',
        },
        website?.summary ||
          (websiteTone === 'passed'
            ? `Contacts published on ${domain} match the email and phone here`
            : `Contacts published on ${domain} do not fully match this record`),
      ),
      next: pick(
        websiteTone,
        {
          input:
            'Waits on the same domain as Domain & contact. Once one is on file, the next run compares the contacts published on the website with the email and phone here.',
          'not-run':
            'The next run compares the contacts published on the website with the email and phone here.',
          passed:
            'The email and phone published on the website match the ones on this record.',
        },
        'Confirm the published contacts with the company and update this record if they differ.',
      ),
      fix: websiteTone === 'input' ? 'domain' : null,
      reads: ['domain', 'email', 'phone'],
      checkedAt: supplier?.website_quality_checked_at || null,
      details: [
        {
          label: 'Accessible',
          value: text(website?.checks?.website_accessible),
        },
        {
          label: 'Email found',
          value: text(website?.checks?.contact_email_found),
        },
        {
          label: 'Phone found',
          value: text(website?.checks?.contact_phone_found),
        },
      ],
    },
    {
      id: 'business-registration',
      label: 'Business registration',
      tone: registrationTone,
      status: statusFor(registrationTone),
      openLabel: openLabelFor(
        registrationTone,
        registration?.is_active === false ? 'Inactive' : 'Failed',
      ),
      about:
        'Whether the company is in the registry, still active, and how long it has been trading',
      description: pick(
        registrationTone,
        {
          unavailable: country
            ? `The registry lookup covers Canadian companies; this record is in ${country}`
            : 'The registry lookup covers Canadian companies, and this record has no country',
          'not-run': supplier?.business_registration_number
            ? 'The registration number has not been checked yet'
            : 'The registry has not been searched for this name yet',
          passed:
            registration?.summary ||
            'Record found in registry · active · registered over 2 years',
          failed:
            registration?.summary ||
            'The registry lists the company as no longer active',
        },
        registration?.summary ||
          (registration?.match_found
            ? 'Found in the registry, but registered under 2 years'
            : 'No matching record was found in the registry'),
      ),
      next: pick(
        registrationTone,
        {
          unavailable: country
            ? `Registry lookups cover Canadian companies only, so this check does not run for a company in ${country}. It does not count against the record.`
            : 'Registry lookups cover Canadian companies only. Add the country to this record; if the company is Canadian, the next run checks the registry.',
          'not-run':
            'The registry is searched by registration number, or by name and country, on the next run.',
          passed:
            'An active registry record under this name and country, trading for more than two years.',
          failed:
            'The registry holds a record for this company that is no longer active. An inactive company cannot trade or sign contracts. Running again will not change this unless the registry does.',
        },
        registration?.match_found
          ? 'The registry holds an active record, but it is less than two years old. Confirm the company’s trading history before clearing it.'
          : 'No registry record matched this name. Add the registration number so the lookup is exact, then run verification again.',
      ),
      fix:
        registrationTone === 'review' && !registration?.match_found
          ? 'registration'
          : registrationTone === 'unavailable' && !country
          ? 'country'
          : null,
      reads: ['name', 'country', 'registration'],
      checkedAt: supplier?.cbr_registry_checked_at || null,
      details: [
        {
          label: 'Registry record found',
          value: text(registration?.match_found),
        },
        { label: 'Active', value: text(registration?.is_active) },
        {
          label: 'Registered over 2 years',
          value: text(registration?.bn_age_pass),
        },
      ],
    },
    {
      id: 'email-verification',
      label: 'Email verification',
      tone: emailTone,
      status: statusFor(emailTone),
      openLabel: openLabelFor(emailTone),
      about: 'Format, MX records, and whether the address is disposable',
      description: pick(
        emailTone,
        {
          input: 'No contact email is on this record',
          'not-run': `${supplier?.email} has not been verified yet`,
        },
        `${supplier?.email} · ${emailFlags.join(', ')}`,
      ),
      next: pick(
        emailTone,
        {
          input:
            'Add a contact email to this record, then run verification again. Its format, mail records and provider are checked.',
          'not-run':
            'The format, mail records and provider are checked on the next run.',
          passed:
            'A valid address with working mail records, from a provider that is not disposable.',
          failed: `${
            email?.disposable || email?.valid_format === false
              ? ''
              : 'The provider flags this address to block. '
          }Mail sent there will not reach the company. Ask the company for a working business address.`,
        },
        `${
          emailReviewReasons.length ? `${emailReviewReasons.join('. ')}. ` : ''
        }Confirm the address with the company before relying on it.`,
      ),
      fix:
        emailTone === 'input'
          ? 'email'
          : emailTone === 'failed'
          ? 'email'
          : null,
      reads: ['email'],
      checkedAt: supplier?.email_verify_checked_at || null,
      details: [
        { label: 'Score', value: text(email?.score) },
        { label: 'Valid format', value: text(email?.valid_format) },
        { label: 'MX records', value: text(email?.has_mx_records) },
      ],
    },
    {
      id: 'phone-validation',
      label: 'Phone validation',
      tone: phoneTone,
      status: statusFor(phoneTone),
      openLabel: openLabelFor(phoneTone),
      about: 'Line type, carrier, and the city the number is registered in',
      description: pick(
        phoneTone,
        {
          input: 'No contact phone is on this record',
          'not-run': `${supplier?.phone} has not been validated yet`,
          failed: `${supplier?.phone} · ${
            phone?.valid === false ? 'not a valid number' : 'flagged for abuse'
          }`,
        },
        `${supplier?.phone}${
          phoneFacts.length ? ` · ${phoneFacts.join(', ')}` : ''
        }`,
      ),
      next: pick(
        phoneTone,
        {
          input:
            'Add a contact phone to this record, then run verification again. Its line type, carrier and registered city are read.',
          'not-run':
            'The line type, carrier and registered city are read on the next run.',
          passed: 'A valid number that is not disposable.',
          failed:
            phone?.valid === false
              ? 'Calls to it will not reach the company. Ask the company for a working phone and update the record.'
              : 'Confirm the number with the company before calling it.',
        },
        `It could not be fully validated${
          phone?.disposable ? ' and is a disposable number' : ''
        }. Confirm it with the company.`,
      ),
      fix: phoneTone === 'input' || phoneTone === 'failed' ? 'phone' : null,
      reads: ['phone'],
      checkedAt: supplier?.phone_verify_checked_at || null,
      details: [
        { label: 'Valid', value: text(phone?.valid) },
        { label: 'Line type', value: text(phone?.line_type) },
        { label: 'Carrier', value: text(phone?.carrier) },
      ],
    },
    {
      id: 'address-facility',
      label: 'Address & facility',
      tone: addressTone,
      status: statusFor(addressTone),
      openLabel: openLabelFor(addressTone),
      about: 'Whether the address is a commercial building or a mail drop',
      description: pick(
        addressTone,
        {
          input: 'No address is on this record',
          'not-run': `${address} has not been validated yet`,
          passed: `${address} · complete address, not a P.O. box`,
        },
        `${address} · ${addressProblem}`,
      ),
      next: pick(
        addressTone,
        {
          input:
            'Add the company’s address to this record, then run verification again. It is matched to a building and checked for P.O. boxes and mail drops.',
          'not-run':
            'The address is matched to a building and checked for P.O. boxes and mail drops on the next run.',
          passed:
            'A complete address matched to a listed building, not a P.O. box.',
        },
        'Confirm where the company operates and update the record if it differs.',
      ),
      fix:
        addressTone === 'input' || addressTone === 'review' ? 'address' : null,
      reads: ['address'],
      checkedAt: supplier?.places_verification_checked_at || null,
      details: [
        {
          label: 'Complete address',
          value: text(
            addressValidation?.address_complete ??
              newAddressChecks?.isCompleteAddress?.value,
          ),
        },
        {
          label: 'P.O. box',
          value: text(
            addressValidation?.is_po_box ?? newAddressChecks?.isPoBox?.value,
          ),
        },
        {
          label: 'Facility found',
          value: text(
            listingMissing ? false : listingFound ? true : undefined,
          ),
        },
      ],
    },
  ];
};

/** The most recent time any check wrote a result to this record. */
export const latestVerificationRun = (
  checks: VerificationReportCheck[],
): string | null => latest(...checks.map((check) => check.checkedAt));

/** The record fields verification reads, in the order the page lists them. */
export const VERIFICATION_RECORD_FIELDS: VerificationRecordField[] = [
  'name',
  'country',
  'registration',
  'email',
  'phone',
  'address',
  'domain',
];

/** Heading over a check's explanation: a finding, a fix, or why it skipped. */
export const verificationNextTitle = (check: VerificationReportCheck): string =>
  check.tone === 'passed' || check.tone === 'failed'
    ? 'What was found'
    : check.tone === 'unavailable'
    ? 'Why it did not run'
    : 'What closes it';

/** A check without a result has no evidence worth listing. */
export const verificationShowsEvidence = (
  check: VerificationReportCheck,
): boolean =>
  check.tone !== 'input' &&
  check.tone !== 'unavailable' &&
  check.tone !== 'not-run';

/** The pill text for a check's result; a stale one says so first. */
export const verificationStatusLabel = (
  check: VerificationReportCheck,
  notUpdated = false,
): string =>
  notUpdated
    ? 'Not updated'
    : check.tone === 'passed'
    ? 'Passed'
    : check.openLabel;
